import { motion, AnimatePresence } from 'framer-motion';
import { X, FileCheck, CheckCircle, FileText, Shield, Award } from 'lucide-react';

interface GestionLicencesSPLAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GestionLicencesSPLAModal({ isOpen, onClose }: GestionLicencesSPLAModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-blue-500/30 shadow-2xl shadow-blue-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-blue-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl">
                    <FileCheck className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                      Gestion des Licences Microsoft (SPLA)
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      SPLA autorisé - Rigueur et conformité garanties
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-blue-600/20 via-indigo-600/20 to-blue-600/20 rounded-xl p-8 border-2 border-blue-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  SPLA Autorisé avec Microsoft
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  BZ Inc. est <strong className="text-white">SPLA autorisé</strong> avec Microsoft. Nous avons une 
                  <strong className="text-blue-400"> rigueur sur la gestion des licences</strong> avec 
                  <strong className="text-indigo-400"> procédures de validation</strong> en place, 
                  <strong className="text-white"> audits réguliers</strong> et 
                  <strong className="text-white"> rapports détaillés</strong>.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-xl p-6 border border-blue-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Award className="w-7 h-7 text-blue-400" />
                    Statut SPLA
                  </h4>
                  <p className="text-gray-300 mb-4">
                    BZ Inc. est officiellement autorisé dans le programme SPLA (Service Provider License Agreement) de Microsoft :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Autorisation officielle</strong> - Partenaire SPLA certifié</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Conformité garantie</strong> - Respect des exigences Microsoft</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Accès aux produits</strong> - Tous les produits Microsoft disponibles</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-7 h-7 text-indigo-400" />
                    Procédures de Validation
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Procédures rigoureuses en place pour la gestion des licences :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Validation des besoins</strong> - Analyse des besoins clients</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Attribution des licences</strong> - Processus structuré</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Documentation complète</strong> - Traçabilité de toutes les licences</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Contrôles réguliers</strong> - Vérification continue</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-7 h-7 text-purple-400" />
                    Audits Réguliers
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Audits réguliers pour garantir la conformité :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Audits internes</strong> - Vérification régulière</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Audits Microsoft</strong> - Conformité avec les exigences</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Correction proactive</strong> - Résolution immédiate des écarts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Amélioration continue</strong> - Optimisation des processus</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-7 h-7 text-green-400" />
                    Rapports Détaillés
                  </h4>
                  <p className="text-gray-300">
                    Rapports détaillés sur l'utilisation des licences pour transparence et conformité :
                  </p>
                  <ul className="space-y-2 text-gray-300 mt-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Rapports d'utilisation mensuels</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Rapports de conformité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Rapports d'audit</span>
                    </li>
                  </ul>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-blue-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












